import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, Sparkles, Zap, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const Planner333New = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [step, setStep] = useState(1); // 1: Form, 2: Ideas Generated
  const [loading, setLoading] = useState(false);
  const [expandingId, setExpandingId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    topic: '',
    business_id: '',
    target_audience: '',
    tone: 'profesional'
  });

  // Ideas state
  const [ideas, setIdeas] = useState([]);
  const [expandedContent, setExpandedContent] = useState({}); // {ideaId: {reel, carousel, story}}

  useEffect(() => {
    loadBusinesses();
  }, []);

  const loadBusinesses = async () => {
    try {
      const response = await apiClient.get('/businesses');
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error loading businesses:', error);
    }
  };

  const handleGenerateIdeas = async (e) => {
    e.preventDefault();
    
    if (!formData.topic) {
      toast.error('Por favor ingresa el tema/nicho de tu negocio');
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        topic: formData.topic,
        business_id: formData.business_id && formData.business_id !== 'none' ? formData.business_id : null,
        target_audience: formData.target_audience || null,
        tone: formData.tone
      };
      
      const response = await apiClient.post('/planner/generate-ideas', payload);
      setIdeas(response.data.mother_ideas);
      setStep(2);
      toast.success('¡9 ideas madre generadas! 🎉 Ahora expande cada una.');
    } catch (error) {
      console.error('Error generating ideas:', error);
      toast.error('Error al generar ideas. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleExpandIdea = async (idea) => {
    if (expandedContent[idea.id]) {
      toast.info('Esta idea ya está expandida');
      return;
    }

    try {
      setExpandingId(idea.id);
      
      const payload = {
        idea_id: idea.id,
        idea_title: idea.title,
        idea_description: idea.description,
        idea_type: idea.type,
        topic: formData.topic,
        tone: formData.tone
      };
      
      const response = await apiClient.post('/planner/expand-idea', payload);
      
      // Store expanded content
      setExpandedContent(prev => ({
        ...prev,
        [idea.id]: response.data.content_pieces
      }));
      
      toast.success(`Idea expandida: ${idea.title} 🎬`);
    } catch (error) {
      console.error('Error expanding idea:', error);
      toast.error('Error al expandir la idea. Intenta nuevamente.');
    } finally {
      setExpandingId(null);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      educativa: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-900', badge: 'bg-blue-200' },
      credibilidad: { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-900', badge: 'bg-purple-200' },
      conexion: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-900', badge: 'bg-green-200' }
    };
    return colors[type] || colors.educativa;
  };

  const getTypeIcon = (type) => {
    const icons = {
      educativa: '📚',
      credibilidad: '🏆',
      conexion: '💚'
    };
    return icons[type] || '✨';
  };

  const renderForm = () => (
    <Card className="max-w-2xl mx-auto bg-white shadow-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-2xl">
          <Sparkles className="w-6 h-6 text-purple-600" />
          Paso 1: Genera 9 Ideas Madre
        </CardTitle>
        <CardDescription className="text-base">
          Primero generamos las 9 ideas base, luego las expandes una por una
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleGenerateIdeas} className="space-y-6">
          <div>
            <Label htmlFor="topic">Tema/Nicho de Tu Negocio *</Label>
            <Input
              id="topic"
              value={formData.topic}
              onChange={(e) => setFormData({...formData, topic: e.target.value})}
              placeholder="Ej: Marketing digital para emprendedores"
              required
            />
          </div>

          <div>
            <Label>Negocio (Opcional)</Label>
            <Select 
              value={formData.business_id} 
              onValueChange={(value) => setFormData({...formData, business_id: value})}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sin personalización" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin personalización</SelectItem>
                {businesses.map((business) => (
                  <SelectItem key={business.id} value={business.id}>
                    {business.business_description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="audience">Audiencia Objetivo (Opcional)</Label>
            <Input
              id="audience"
              value={formData.target_audience}
              onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
              placeholder="Ej: Emprendedores de 25-40 años"
            />
          </div>

          <div>
            <Label>Tono de Voz</Label>
            <Select 
              value={formData.tone} 
              onValueChange={(value) => setFormData({...formData, tone: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="profesional">Profesional</SelectItem>
                <SelectItem value="casual">Casual</SelectItem>
                <SelectItem value="inspiracional">Inspiracional</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button type="submit" className="w-full h-14 text-lg" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                Generando 9 ideas madre (10-20 segundos)...
              </>
            ) : (
              <>
                <Zap className="mr-2 w-5 h-5" />
                Generar 9 Ideas Madre
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );

  const renderIdea = (idea) => {
    const colors = getTypeColor(idea.type);
    const icon = getTypeIcon(idea.type);
    const isExpanded = !!expandedContent[idea.id];
    const isExpanding = expandingId === idea.id;
    const contentPieces = expandedContent[idea.id] || [];

    return (
      <Card key={idea.id} className={`${colors.border} border-2`}>
        <CardHeader className={colors.bg}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{icon}</span>
                <span className={`px-3 py-1 ${colors.badge} rounded-full text-sm font-semibold capitalize`}>
                  {idea.type}
                </span>
              </div>
              <CardTitle className={`text-xl ${colors.text}`}>{idea.title}</CardTitle>
              <CardDescription className="mt-2 text-base">
                {idea.description}
              </CardDescription>
              <div className={`mt-3 p-3 bg-white rounded-lg border ${colors.border}`}>
                <p className="font-semibold text-sm">💡 Mensaje clave:</p>
                <p className="text-sm mt-1">{idea.key_message}</p>
              </div>
            </div>
            <div className="ml-4">
              {isExpanded ? (
                <div className="flex flex-col items-center gap-2">
                  <CheckCircle2 className="w-12 h-12 text-green-600" />
                  <span className="text-xs text-green-700 font-semibold">Expandida</span>
                </div>
              ) : (
                <Button
                  onClick={() => handleExpandIdea(idea)}
                  disabled={isExpanding}
                  size="lg"
                  className="h-12"
                >
                  {isExpanding ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Expandiendo...
                    </>
                  ) : (
                    <>
                      <Zap className="mr-2 w-4 h-4" />
                      Expandir a 3 Formatos
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="pt-6">
            <div className="space-y-4">
              <h4 className="font-bold text-lg mb-4">🎬 3 Formatos de Contenido:</h4>
              
              {contentPieces.map((piece) => (
                <div key={piece.id} className="bg-gray-50 p-4 rounded-lg border-2">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">
                      {piece.format_type === 'reel' ? '🎥' : piece.format_type === 'carousel' ? '📊' : '📱'}
                    </span>
                    <h5 className="font-bold capitalize">{piece.format_type}</h5>
                    <span className="text-sm text-gray-600">• {piece.day_suggestion}</span>
                  </div>

                  {piece.format_type === 'reel' && (
                    <div className="space-y-2 text-sm">
                      <div><strong>Hook:</strong> {piece.content.hook}</div>
                      <div><strong>Desarrollo:</strong> {piece.content.development}</div>
                      <div><strong>CTA:</strong> {piece.content.cta}</div>
                      <div><strong>Visual:</strong> <em>{piece.content.visual_description}</em></div>
                    </div>
                  )}

                  {piece.format_type === 'carousel' && (
                    <div className="space-y-2 text-sm">
                      <div><strong>Slides:</strong> {piece.content.slides?.length || 8} slides</div>
                      <div><strong>Portada:</strong> <em>{piece.content.cover_description}</em></div>
                      <div className="max-h-32 overflow-y-auto">
                        <strong>Caption:</strong> {piece.content.caption}
                      </div>
                    </div>
                  )}

                  {piece.format_type === 'story' && (
                    <div className="space-y-2 text-sm">
                      <div><strong>Texto:</strong> {piece.content.text}</div>
                      <div><strong>Interacción:</strong> {piece.content.interaction_type}</div>
                      <div><strong>Visual:</strong> <em>{piece.content.visual_description}</em></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  const renderIdeas = () => {
    const educativas = ideas.filter(i => i.type === 'educativa');
    const credibilidad = ideas.filter(i => i.type === 'credibilidad');
    const conexion = ideas.filter(i => i.type === 'conexion');
    const totalExpanded = Object.keys(expandedContent).length;

    return (
      <div className="space-y-8">
        <Card className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <CardHeader>
            <CardTitle className="text-3xl">
              Paso 2: Expande Cada Idea a 3 Formatos
            </CardTitle>
            <CardDescription className="text-purple-100 text-lg">
              Haz clic en "Expandir" para generar Reel + Carrusel + Story para cada idea
            </CardDescription>
            <div className="mt-4 flex items-center gap-4">
              <div className="bg-white/20 px-4 py-2 rounded-lg">
                <span className="text-2xl font-bold">{totalExpanded}/9</span>
                <span className="text-sm ml-2">ideas expandidas</span>
              </div>
              {totalExpanded === 9 && (
                <div className="bg-green-500 px-4 py-2 rounded-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-semibold">¡Plan 3-3-3 Completo!</span>
                </div>
              )}
            </div>
          </CardHeader>
        </Card>

        <div className="space-y-6">
          <h3 className="text-2xl font-bold flex items-center gap-2">
            📚 Ideas Educativas (3)
          </h3>
          {educativas.map(renderIdea)}
        </div>

        <div className="space-y-6">
          <h3 className="text-2xl font-bold flex items-center gap-2">
            🏆 Ideas de Credibilidad (3)
          </h3>
          {credibilidad.map(renderIdea)}
        </div>

        <div className="space-y-6">
          <h3 className="text-2xl font-bold flex items-center gap-2">
            💚 Ideas de Conexión Humana (3)
          </h3>
          {conexion.map(renderIdea)}
        </div>

        <div className="flex justify-center gap-4 mt-12">
          <Button 
            onClick={() => {
              setStep(1);
              setIdeas([]);
              setExpandedContent({});
            }}
            variant="outline"
            size="lg"
          >
            Generar Nuevas Ideas
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            size="sm"
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Planificador 3-3-3
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            9 ideas madre → Expande cada una → 27 piezas de contenido listas
          </p>
        </div>

        {step === 1 && renderForm()}
        {step === 2 && renderIdeas()}
      </div>
    </div>
  );
};

export default Planner333New;
